import {
  canEdit,
  type CommandInput,
  type CommandKind,
} from '../../../../packages/domain/src/index';
import { episodeCommandAllowance } from '../../../../packages/domain/src/production-plan';
import type { ProviderProfile } from '../../../../packages/domain/src/provider-profiles';
import type {
  CommandView,
  HomeView,
  JobView,
  ProjectDetail,
} from '../../../../packages/domain/src/view';
import { activeStatuses } from '../shared';

/** The same guards used to enable controls and to submit the series' next request. */
export function seriesProductionState(
  home: HomeView | undefined,
  detail: ProjectDetail,
  busy: boolean,
) {
  const writable = !!home && canEdit(home.access.role);
  const running = detail.commands.some((c) => activeStatuses.includes(c.status));
  const available = home
    ? Math.max(
        0,
        Math.min(
          home.budget.cap - home.budget.spent - home.budget.reserved,
          detail.trialRemaining ?? Infinity,
          home.allowance
            ? home.allowance.cap - home.allowance.spent - home.allowance.reserved
            : Infinity,
        ),
      )
    : 0;
  const generationUnavailable =
    detail.generationUnavailable ??
    (home?.allowanceRequired && !home.allowance
      ? 'An active owner allowance is required. Contact the Lab owner.'
      : home?.allowance && !home.allowance.active
        ? 'Your production allowance is inactive. Contact the Lab owner.'
        : home?.budget.suspended
          ? 'Production is paused for budget reconciliation. Contact the Lab owner.'
          : home && home.budget.storageBytes > 8_000_000_000
            ? 'The storage safety limit has been reached. Contact the Lab owner.'
            : home && available <= 0
              ? 'No production allowance remains. Contact the Lab owner.'
              : undefined);
  return {
    writable,
    running,
    available,
    generationUnavailable,
    controlsDisabled: busy || running || !writable || !!generationUnavailable,
  };
}

export function seriesRequestInput(
  projectId: string,
  episodeId: string | undefined,
  kind: CommandKind,
  profile: ProviderProfile | undefined,
  extra: Record<string, string | number | boolean> = {},
): CommandInput {
  return {
    requestId: crypto.randomUUID(),
    projectId,
    episodeId,
    kind,
    ...extra,
    ...(typeof extra.maxCostMicros === 'number'
      ? { maxCostMicros: episodeCommandAllowance(extra.maxCostMicros, profile) }
      : {}),
  };
}

export function productionWorkMessage(
  active: CommandView | undefined,
  home?: HomeView,
  jobs: JobView[] = [],
) {
  if (active?.status === 'RECONCILIATION') return 'A provider result needs verification';
  if (active?.status === 'PAUSED_BUDGET')
    return active.error ?? 'Production is paused at its spending limit';
  if (active?.status === 'PAUSED_QUOTA') {
    const failure = jobs.filter((j) => j.commandId === active._id && j.error).at(-1)?.error;
    return (
      active.error ??
      failure ??
      (active.pauseReason === 'provider-credit'
        ? 'Provider credit is exhausted. Contact the Lab owner.'
        : 'Production is paused. Review the recorded cause in History.')
    );
  }
  return home?.workerOnline
    ? 'The production room is working'
    : home?.executorTransport === 'trigger'
      ? 'The production session will start automatically'
      : 'Waiting for your local worker';
}

export function executorStatus(home: HomeView | undefined, isDemo: boolean) {
  if (isDemo) return { title: 'Demo mode', description: 'Explore without generating' };
  if (home?.executorTransport === 'trigger')
    return {
      title: home.workerOnline ? 'Production session active' : 'On-demand production',
      description: 'The coordinator starts a session when work is queued',
    };
  return {
    title: home?.workerOnline ? 'Local worker online' : 'Local worker offline',
    description: home?.workerOnline
      ? 'Ready to pick up production'
      : 'Production waits for your computer',
  };
}
